import Const from 'beek-design-system/config/Const';

import {
  DeviceSizeMap,
  DeviceSizeOrder,
  widthToWindowSizeName,
} from 'beek-design-system/defaults/DeviceSizes';
import {
  ImageConfigSizeType,
  ImageDimensions,
  UrlConfigSize,
  constructWidthSizeMap,
  getConfigSizeFromType,
} from './imageConfigSizes';

export type ImageSourceType = {
  media: string;
  srcset: string;
  key: string;
};

export type FallbackSourceType = {
  src: string;
  srcset: string;
};

export type ImageSourcesProps = {
  url: string;
  type?: string;
  laptopUrl?: string;
  width?: number;
  height?: number;
  sizeConfig?: ImageConfigSizeType;
  urlsConfig?: UrlConfigSize;
  quality?: number;
  crop?: string;
};

export type ImageFallbackProps = {
  fallbackUrl?: string;
  type?: string;
  width?: number;
  height?: number;
  sizeConfig?: ImageConfigSizeType;
  quality?: number;
  crop?: string;
};

export type GetImageDimensionsProps = {
  type?: string;
  width?: number;
  height?: number;
  sizeConfig?: ImageConfigSizeType;
  currentWidth?: number;
};

export type UrlParts = {
  protocol: string;
  hostname: string;
  path: string;
  queryParams: string;
  hash: string;
  provider: string;
};

type FormatRenderType = {
  format: string;
  priority: number;
};

const highestPriority = 0;
const formatsToRender: FormatRenderType[] = [
  {
    format: 'webp',
    priority: highestPriority,
  },
  {
    format: 'jpg',
    priority: 1,
  },
];
const resolutionsToRender: number[] = [1, 2];

const getUrlParts = (url: string): UrlParts => {
  const urlObject = new URL(url);
  const domainElements = urlObject.hostname.split('.');

  let provider = '';
  if (domainElements.length > 2) {
    provider = domainElements[1];
  } else {
    provider = domainElements[0];
  }

  return {
    protocol: urlObject.protocol,
    hostname: urlObject.hostname,
    path: urlObject.pathname,
    queryParams: urlObject.search,
    hash: urlObject.hash,
    provider,
  };
};

const getBaseUrl = (urlParts: UrlParts): string => {
  if (urlParts.provider === 'googleapis') {
    return `${Const.imageCdnUrl}/image`;
  }
  if (urlParts.provider === 'beek') {
    return `${Const.imageCdnUrl}/image`;
  }
  if (urlParts.provider === 'findawayworld') {
    return `https://images.findawayworld.com`;
  }

  return `${urlParts.protocol}://${urlParts.hostname}`;
};

const createSourceUrl = (
  urlParts: UrlParts,
  width: number,
  height: number,
  quality: number,
  format: string,
  resolution: number,
  crop: string
): string => {
  const newBaseUrl: string = getBaseUrl(urlParts);
  const newHeight = height * resolution;
  const newWidth = width * resolution;

  let newQueryParams = '';
  if (urlParts.provider === 'beek') {
    newQueryParams = `?width=${newWidth}&height=${newHeight}&quality=${quality}&format=${format}&crop=${crop}`;
  }
  if (urlParts.provider === 'googleapis') {
    newQueryParams = `?width=${newWidth}&height=${newHeight}&quality=${quality}&format=${format}&crop=${crop}`;
  }
  if (urlParts.provider === 'findawayworld') {
    newQueryParams = `?width=${newWidth}&height=${newHeight}&quality=${quality}&format=jpeg`;
  }

  return `${newBaseUrl}${urlParts.path}${newQueryParams}`;
};

const constructSources = (
  urlsToRender: UrlConfigSize,
  widthsToRender: ImageConfigSizeType,
  crop: string,
  quality: number
): ImageSourceType[] => {
  const sources: ImageSourceType[] = [];
  const formats: FormatRenderType[] = formatsToRender.sort(
    (first: FormatRenderType, second: FormatRenderType): number => {
      if (first.priority > second.priority) {
        return -1;
      }
      if (first.priority < second.priority) {
        return 1;
      }
      return 0;
    }
  );

  let currentUrl = '';
  DeviceSizeOrder.forEach((key_size: string) => {
    formats.forEach((formatObj) => {
      if (widthsToRender.sizes[key_size]) {
        if (urlsToRender[key_size]) {
          currentUrl = urlsToRender[key_size];
        }
        const imageSize: ImageDimensions = widthsToRender.sizes[key_size];
        const currentWidth = imageSize.width;
        const media = `(min-width: ${DeviceSizeMap[key_size]})`;
        let srcset = '';
        const urlParts: UrlParts = getUrlParts(currentUrl);

        resolutionsToRender.forEach((resolution: number, index: number) => {
          const sourceUrl = createSourceUrl(
            urlParts,
            currentWidth,
            imageSize.height,
            quality,
            formatObj.format,
            resolution,
            crop
          );
          srcset += `${sourceUrl} ${resolution}x`;
          if (index < resolutionsToRender.length - 1) {
            srcset += ', ';
          }
        });

        sources.unshift({
          key: `${DeviceSizeMap[key_size]}-${formatObj.format}`,
          media,
          srcset,
        });
      }
    });
  });

  return sources;
};

const constructWidthsToRender = (
  type?: string,
  width?: number,
  height?: number,
  sizeConfig?: ImageConfigSizeType
): ImageConfigSizeType => {
  let widthsToRender: ImageConfigSizeType = getConfigSizeFromType('default');
  if (width && height) {
    widthsToRender = {
      sizes: {
        mobileS: {
          width,
          height,
        },
      },
      fallbackImage: '',
    };
  } else if (sizeConfig) {
    widthsToRender = sizeConfig;
  } else if (type) {
    widthsToRender = getConfigSizeFromType(type);
  }
  return widthsToRender;
};

export const getCurrentImageDimensions = (
  props: GetImageDimensionsProps
): [number, number] => {
  const { type, width, height, sizeConfig, currentWidth } = props;

  const widthsToRender: ImageConfigSizeType = constructWidthsToRender(
    type,
    width,
    height,
    sizeConfig
  );

  if (currentWidth) {
    const sizeMap = constructWidthSizeMap(widthsToRender);
    const imageSizeKey = widthToWindowSizeName(currentWidth, sizeMap);
    const size = widthsToRender.sizes[imageSizeKey];

    return [size.width, size.height];
  }

  const size = widthsToRender.sizes.mobileS;
  return [size.width, size.height];
};

export const getImageSources = (
  props: ImageSourcesProps
): ImageSourceType[] => {
  const {
    url,
    type,
    laptopUrl,
    width,
    height,
    sizeConfig,
    urlsConfig,
    quality,
    crop,
  } = props;
  const newCrop = crop || 'cover';
  const newQuality = quality || 75;

  let urlsToRender: UrlConfigSize = { mobileS: url };
  if (laptopUrl) {
    urlsToRender = { mobileS: url, laptop: laptopUrl };
  } else if (urlsConfig) {
    urlsToRender = urlsConfig;
  }

  const widthsToRender: ImageConfigSizeType = constructWidthsToRender(
    type,
    width,
    height,
    sizeConfig
  );
  const sources: ImageSourceType[] = constructSources(
    urlsToRender,
    widthsToRender,
    newCrop,
    newQuality
  );

  return sources;
};

export const getImageFallback = (
  props: ImageFallbackProps
): FallbackSourceType => {
  const { fallbackUrl, type, width, height, sizeConfig } = props;

  let url = getConfigSizeFromType('default').fallbackImage;
  if (fallbackUrl) {
    url = fallbackUrl;
  } else if (sizeConfig) {
    url = sizeConfig.fallbackImage;
  } else if (type) {
    url = getConfigSizeFromType(type).fallbackImage;
  }
  const urlsToRender: UrlConfigSize = { mobileS: url };

  const widthsToRender: ImageConfigSizeType = constructWidthsToRender(
    type,
    width,
    height,
    sizeConfig
  );
  const sources: ImageSourceType[] = constructSources(
    urlsToRender,
    widthsToRender,
    'cover',
    75
  );

  const fallback: FallbackSourceType = {
    src: url,
    srcset: sources[0].srcset,
  };

  return fallback;
};

export default getImageSources;
